/**
 * OAuth 콜백 처리 컴포넌트
 * 백엔드 OAuth 인증 후 리다이렉트되는 페이지
 * Firebase Authentication 통합
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../firebase';
import CONFIG from '../config';
import { registerSession } from '../services/sessionService';

function OAuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('처리 중...');

  // 세션 충돌 모달 상태 (다중 로그인 허용으로 현재 비활성화)
  const [showSessionModal, setShowSessionModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [existingSessionInfo, setExistingSessionInfo] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [pendingLoginData, setPendingLoginData] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // URL 파라미터에서 customToken 추출
        const params = new URLSearchParams(location.search);

        // 에러 체크
        const error = params.get('error');
        if (error) {
          setStatus(`로그인 실패: ${error}`);
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        // Custom Token 추출
        const customToken = params.get('customToken');
        const provider = params.get('provider');
        const isNewUser = params.get('isNewUser') === 'true';

        if (!customToken) {
          setStatus('인증 토큰이 없습니다');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        // setStatus('Firebase 인증 중...');
        console.log(`🔐 ${provider} OAuth 인증 시작`);

        // Firebase Custom Token으로 로그인
        const userCredential = await signInWithCustomToken(auth, customToken);
        const user = userCredential.user;

        console.log('✅ Firebase 인증 성공:', user.uid);
        console.log('✅ 사용자 정보:', {
          uid: user.uid,
          displayName: user.displayName,
          email: user.email
        });

        // ID Token 가져오기
        setStatus('세션 생성 중...');
        const idToken = await user.getIdToken();
        console.log('✅ ID Token 획득 완료');

        // 백엔드에 ID Token을 전송하여 Session Cookie 생성
        const sessionResponse = await fetch(`${CONFIG.CLOUD_FUNCTION_URL}/set_session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Cookie 송수신 활성화
          body: JSON.stringify({ idToken })
        });

        if (!sessionResponse.ok) {
          const errorData = await sessionResponse.json();
          throw new Error(errorData.error || 'Session 생성 실패');
        }

        const sessionResult = await sessionResponse.json();
        console.log('✅ Session Cookie 생성 완료:', sessionResult);

        // 다중 로그인 허용 - 세션 체크 비활성화
        // const existingSession = await checkExistingSession(user.uid);
        // if (existingSession) {
        //   // 다른 기기에서 로그인 중 → 모달 표시
        //   setExistingSessionInfo(existingSession);
        //   setPendingLoginData({ userId: user.uid, isNewUser });
        //   setShowSessionModal(true);
        //   setStatus('');
        //   return;
        // }

        // 세션 등록 (다중 로그인 허용이지만 기록은 유지)
        await registerSession(user.uid);

        // 신규 사용자는 브랜드킷 페이지로, 기존 사용자는 메인 페이지로 이동
        if (isNewUser) {
          navigate('/main', { state: { openBrandKit: true, isNewUser: true } });
        } else {
          navigate('/main');
        }

      } catch (error) {
        console.error('❌ OAuth 콜백 처리 오류:', error);
        setStatus('로그인 처리 중 오류가 발생했습니다');
        setTimeout(() => navigate('/login'), 2000);
      }
    };

    processCallback();
  }, [location, navigate]);

  // 세션 충돌 시 계속 진행 (기존 세션 로그아웃)
  const handleContinueLogin = async () => {
    if (!pendingLoginData) return;

    try {
      await registerSession(pendingLoginData.userId);
      setShowSessionModal(false);

      if (pendingLoginData.isNewUser) {
        navigate('/main', { state: { openBrandKit: true, isNewUser: true } });
      } else {
        navigate('/main');
      }
    } catch (error) {
      console.error('세션 등록 오류:', error);
      setStatus('로그인 처리 중 오류가 발생했습니다');
      setTimeout(() => navigate('/login'), 2000);
    }
  };

  // 세션 충돌 시 취소 (로그인 취소)
  const handleCancelLogin = async () => {
    setShowSessionModal(false);
    await auth.signOut();
    navigate('/login');
  };

  return (
    <div style={containerStyle}>
      {/* 세션 충돌 모달 */}
      {showSessionModal && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', color: '#1f2937' }}>
              다른 기기에서 로그인 중
            </h2>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
              현재 <strong style={{ color: '#1A62EA' }}>{existingSessionInfo?.deviceInfo || '다른 기기'}</strong>에서
              로그인 중입니다.<br />
              해당 기기를 로그아웃하고 여기서 계속하시겠습니까?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={handleCancelLogin} style={cancelButtonStyle}>
                취소
              </button>
              <button onClick={handleContinueLogin} style={confirmButtonStyle}>
                로그아웃하고 계속하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 로딩 카드 */}
      {!showSessionModal && (
        <div style={cardStyle}>
          <div style={spinnerContainerStyle}>
            <div style={spinnerStyle}></div>
          </div>
          <h2 style={titleStyle}>{status}</h2>
          <p style={subtitleStyle}>잠시만 기다려주세요</p>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

const containerStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#fefefe'
};

const cardStyle = {
  backgroundColor: 'white',
  borderRadius: '20px',
  padding: '40px',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  textAlign: 'center',
  maxWidth: '400px',
  width: '100%'
};

const spinnerContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '20px'
};

const spinnerStyle = {
  width: '50px',
  height: '50px',
  border: '4px solid #f3f3f3',
  borderTop: '4px solid #4f46e5',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite'
};

const titleStyle = {
  fontSize: '20px',
  fontWeight: '600',
  color: '#1a2854',
  marginBottom: '10px'
};

const subtitleStyle = {
  fontSize: '14px',
  color: '#6b7280'
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000
};

const modalStyle = {
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '28px',
  maxWidth: '420px',
  width: '90%',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
};

const cancelButtonStyle = {
  padding: '10px 20px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  backgroundColor: '#f9fafb',
  color: '#374151',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer'
};

const confirmButtonStyle = {
  padding: '10px 20px',
  border: 'none',
  borderRadius: '8px',
  background: 'linear-gradient(135deg, #1A62EA 0%, #3b82f6 100%)',
  color: 'white',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(26, 98, 234, 0.3)'
};

export default OAuthCallback;
